import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { isProviderRiskFilterGroup, withLatestRiskSnapshot } from '@app/src/utils/providerRiskFilter'
import { useFlags } from 'launchdarkly-react-client-sdk'

/**
 * The provider filters behind the current URL, as the API should receive them.
 *
 * Overall risk is the reason this exists rather than useGetApiQueryFilters directly: the filter bar
 * emits one group per URL parameter, so the risk status arrives without the isLatest companion that
 * pins it to the current snapshot. Every reader of the provider filters needs that pairing - the
 * table, the counts, the exports and the spend-import template - so it happens here rather than at
 * each of them.
 *
 * A risk filter also drops out entirely when topicalRisk is off: the filter stays in allowedFilters,
 * so a shared URL could otherwise keep the table and its exports narrowed with no control on screen
 * to clear it.
 */
export const useProviderApiQueryFilters = (allowedFilters: string[]): FilterGroup[] => {
  const { topicalRisk = false } = useFlags()
  const filters = useGetApiQueryFilters(allowedFilters)

  return topicalRisk
    ? withLatestRiskSnapshot(filters)
    : filters.filter(group => !isProviderRiskFilterGroup(group))
}
